/**
 * Centralized order type determination logic
 * Single point of truth for LIMIT vs STOPLIMIT classification
 */

/**
 * Determines order type based on order price vs current market price
 *
 * @param {string} side - Order side ('buy' or 'sell')
 * @param {number} orderPrice - The order price
 * @param {number} currentPrice - Current market price
 * @returns {string} - 'LIMIT', 'STOPLIMIT', or 'MARKET'
 */
export const determineOrderType = (side, orderPrice, currentPrice) => {
  // If no current price available, default to MARKET
  if (currentPrice === null || currentPrice === undefined) {
    return "MARKET";
  }

  // If order price is zero or negative, it's a market order
  if (orderPrice <= 0) {
    return "MARKET";
  }

  if (side === "buy") {
    // For buy orders: price <= current = LIMIT, price > current = STOPLIMIT
    return orderPrice <= currentPrice ? "LIMIT" : "STOPLIMIT";
  } else if (side === "sell") {
    // For sell orders: price >= current = LIMIT, price < current = STOPLIMIT
    return orderPrice >= currentPrice ? "LIMIT" : "STOPLIMIT";
  }

  // Fallback for unknown side
  return "MARKET";
};

/**
 * Check if order type should change when price crosses current market price
 *
 * @param {string} side - Order side ('buy' or 'sell')
 * @param {number} orderPrice - The order price
 * @param {number} oldCurrentPrice - Previous market price
 * @param {number} newCurrentPrice - New market price
 * @returns {boolean} - True if order type should change
 */
export const shouldOrderTypeCrossover = (
  side,
  orderPrice,
  oldCurrentPrice,
  newCurrentPrice,
) => {
  if (oldCurrentPrice === null || newCurrentPrice === null) {
    return false;
  }

  if (side === "buy") {
    // Check if order price crossed from one side of market to the other
    const wasAbove = orderPrice > oldCurrentPrice;
    const isAbove = orderPrice > newCurrentPrice;
    return wasAbove !== isAbove;
  } else if (side === "sell") {
    // Check if order price crossed from one side of market to the other
    const wasBelow = orderPrice < oldCurrentPrice;
    const isBelow = orderPrice < newCurrentPrice;
    return wasBelow !== isBelow;
  }

  return false;
};
